import { Check } from 'lucide-react';
import clsx from 'clsx';
import { flexionar, type Sexo } from '@social/shared';
import { URL_APP } from '../dados/navegacao';
import type { Plano } from '../lib/precos';
import { formatarCentavos, formatarPlano } from '../lib/precos';
import { Botao } from './botao';

/**
 * "10 sociedades" ou "Sociedades ilimitadas".
 *
 * `null` é o ilimitado do catálogo. Escrito por extenso e não como traço: um
 * traço faria o plano mais caro parecer o que oferece menos.
 *
 * O gênero vem de quem chama (regra 9: nenhuma flexão escrita à mão no call
 * site) — "usuários ilimitados", mas "sociedades ilimitadas". Foi exatamente
 * o defeito publicado: "Usuários ilimitadas" numa página que vende
 * concordância gramatical.
 */
export function quantos(
  teto: number | null,
  singular: string,
  plural: string,
  genero: Sexo,
): string {
  if (teto === null) {
    const ilimitado = flexionar(
      { sexo: genero, numero: 'PLURAL' },
      'ilimitados',
      'ilimitadas',
    );
    return `${plural.charAt(0).toUpperCase()}${plural.slice(1)} ${ilimitado}`;
  }
  return `${String(teto)} ${teto === 1 ? singular : plural}`;
}

type PlanoCartaoProps = {
  plano: Plano;
  destaque?: boolean;
  /**
   * O preço MENSAL do mesmo nível, em centavos — a âncora do desconto.
   *
   * Sem ela, "R$ 69,90/mês" e "R$ 838,80 de uma vez" são consistentes entre
   * si (838,80 = 69,90×12, o desconto já está embutido) mas o "30% off" do
   * seletor parece propaganda que o número desmente: falta o "de R$ 99,90"
   * que mostra de ONDE o preço caiu. Só é exibida quando o prazo escolhido
   * não é o mensal — comparar o mensal com ele mesmo não diz nada.
   */
  referenciaMensalCentavos?: number | undefined;
};

export function PlanoCartao({
  plano,
  destaque = false,
  referenciaMensalCentavos,
}: PlanoCartaoProps): JSX.Element {
  const formatado = formatarPlano(plano);

  return (
    <div
      className={clsx(
        'rounded-auth-card cartao-reativo relative flex h-full flex-col gap-6 border p-7',
        destaque
          ? 'border-auth-marca-hover/40 bg-auth-superficie/[0.05] hover:border-auth-marca-hover/60'
          : 'border-auth-borda/8 bg-auth-superficie/[0.02] hover:border-auth-borda/20',
      )}
    >
      {destaque ? (
        <span className="bg-auth-marca-forte text-auth-marca-contraste rounded-auth-controle absolute -top-3 left-7 px-2.5 py-1 text-xs font-medium">
          Melhor custo
        </span>
      ) : null}

      <div className="flex flex-col gap-1">
        <p className="text-auth-texto-terciario text-xs font-medium tracking-[0.14em] uppercase">
          {plano.nome}
        </p>
        {/*
          `key` no CÓDIGO do plano: trocar o prazo remonta este bloco, e a
          animação de entrada roda do zero. É o que um `AnimatePresence
          mode="wait"` faria — sem a saída, que aqui não faz falta: o número
          que sai já não interessa a ninguém.

          O cartão em volta NÃO remonta (a `key` dele é o nível), então a
          troca de prazo anima só o preço, e não a fileira inteira.
        */}
        {plano.periodicidade !== 'MENSAL' && referenciaMensalCentavos ? (
          <p
            key={`${plano.codigo}-ancora`}
            className="preco-troca text-auth-texto-terciario text-sm tabular-nums"
          >
            de <s>{formatarCentavos(referenciaMensalCentavos)}/mês</s> por
          </p>
        ) : null}
        <p
          key={plano.codigo}
          className="preco-troca text-auth-texto text-3xl font-semibold tracking-tight tabular-nums"
        >
          {formatado.precoMensalEquivalente}
        </p>
        {plano.periodicidade !== 'MENSAL' ? (
          <p
            key={`${plano.codigo}-ciclo`}
            className="preco-troca text-auth-texto-secundario text-xs"
          >
            {formatado.precoCobrado}
          </p>
        ) : null}
      </div>

      {/*
        Os LIMITES, e não uma lista de qualidades.

        Aqui ficava "Sociedades ilimitadas no plano" com um TODO dizendo que os
        limites reais ainda não tinham sido definidos — e desde que passaram a
        existir, aquela linha virou mentira nos dois planos que têm teto. O que
        diferencia um nível do outro é justamente o número: sem ele a página
        tem três preços e nenhuma razão para escolher.

        `null` é ILIMITADO, e é escrito por extenso: um traço ou um vazio faria
        o plano mais caro parecer o que oferece menos.
      */}
      <ul className="flex flex-col gap-2">
        {[
          quantos(plano.limites.sociedades, 'sociedade', 'sociedades', 'F'),
          quantos(plano.limites.usuarios, 'usuário', 'usuários', 'M'),
          `${quantos(plano.limites.atosPorMes, 'ato', 'atos', 'M')} por mês`,
          'Contrato, alteração e consolidação em .docx e PDF',
        ].map((item) => (
          <li
            key={item}
            className="text-auth-texto-secundario flex items-start gap-2 text-sm"
          >
            <Check
              size={16}
              className="text-auth-marca-hover mt-0.5 shrink-0"
              aria-hidden
            />
            {item}
          </li>
        ))}
      </ul>

      {/* Mesmo destino do herói: o autocadastro EXISTE (rota `/criar-conta`
          do app). Este botão apontava para `#contato` de quando não existia —
          quem decidia assinar num card era mandado para a captura de e-mail. */}
      <Botao
        href={`${URL_APP}/criar-conta`}
        variante={destaque ? 'primario' : 'fantasma'}
        className="mt-auto"
      >
        Começar teste grátis
      </Botao>
    </div>
  );
}
